import {
  freecell,
  minesweeper,
  noughts,
  RULE_PRESETS,
  solitaire,
  spider,
  type GameSummary,
  type GameType,
} from '@cardengine/shared';
import { JoinPanel } from '../components/JoinPanel.js';
import { SoloStartPanel, type SoloPreset } from '../components/SoloStartPanel.js';
import { linkProps } from '../router.js';

/** What a game's own page needs to say about itself, beyond the catalogue entry. */
interface SoloCopy {
  gameType: GameType;
  presets: readonly SoloPreset[];
  /** Legend over the preset row, in the panel. */
  choiceLabel: string;
  /** Heading for the presets section further down the page. */
  presetsTitle: string;
  presetsIntro: string;
  /** Only for a solo game that is not dealt from a deck. See `SoloStartPanel`. */
  verb?: { heading: string; button: string; busy: string };
}

/**
 * Keyed by catalogue id, which is also the `GameType`. A game absent from here
 * is a multiplayer one, and gets the join panel.
 */
const SOLO: Record<string, SoloCopy> = {
  solitaire: {
    gameType: 'solitaire',
    presets: solitaire.RULE_PRESETS,
    choiceLabel: 'Difficulty',
    presetsTitle: 'Pick your poison',
    presetsIntro:
      'How hard a deal is comes down to the stock: how many cards you turn at a time, and how many ' +
      'times you may go back through it. Pick a preset here, or change the details once you have sat down.',
  },
  freecell: {
    gameType: 'freecell',
    presets: freecell.RULE_PRESETS,
    choiceLabel: 'Cells',
    presetsTitle: 'How much room to think',
    presetsIntro:
      'Nothing is hidden, so the difficulty is entirely in how much space you are given: four cells ' +
      'to park cards in, or fewer. Pick a preset here, or change the details once you have sat down.',
  },
  spider: {
    gameType: 'spider',
    presets: spider.RULE_PRESETS,
    choiceLabel: 'Suits',
    presetsTitle: 'One suit, or four',
    presetsIntro:
      'Cards stack by rank whatever their suit, but only a run in one suit moves as a piece — so how ' +
      'many suits are in the pack decides how much of what you build will actually travel. That is ' +
      'the whole difficulty curve. Pick a preset here, or change it once you have sat down.',
  },
  minesweeper: {
    gameType: 'minesweeper',
    presets: minesweeper.RULE_PRESETS,
    choiceLabel: 'Board',
    presetsTitle: 'Three boards, and anything between',
    presetsIntro:
      'What makes a board hard is not how many mines are on it but how tightly they are packed. ' +
      'Beginner is one square in eight and can be read out with patience; expert is one in five, ' +
      'which is where boards start refusing to give you a certain move. Pick one here, or set the ' +
      'grid and the mine count yourself once you have sat down.',
    verb: { heading: 'Start a board of', button: 'Start', busy: 'Laying the board…' }
  },
};

/**
 * Each game's presets, keyed by catalogue id.
 *
 * Every game has some, including the seated ones, so the section below reads
 * from here rather than falling back to Switch's — which it used to, and which
 * would have shown a noughts and crosses page three ways to play Switch.
 */
const PRESETS: Record<string, readonly { id: string; name: string; description: string }[]> = {
  switch: RULE_PRESETS,
  solitaire: solitaire.RULE_PRESETS,
  freecell: freecell.RULE_PRESETS,
  spider: spider.RULE_PRESETS,
  'noughts-and-crosses': noughts.RULE_PRESETS,
  minesweeper: minesweeper.RULE_PRESETS,
};

/** A game's own page: what it is, and the way in. */
export function GamePage({ game }: { game: GameSummary }) {
  const solo = SOLO[game.id];
  const presets = PRESETS[game.id] ?? RULE_PRESETS;

  return (
    <main className="page">
      <section className="gamehero">
        <div className="gamehero__text">
          <p className="gamehero__eyebrow">
            {solo ? 'One player · no waiting' : `${game.minPlayers}–${game.maxPlayers} players · people or AI`}
          </p>
          <h1 className="gamehero__title">{game.name}</h1>
          <p className="gamehero__blurb">{game.description}</p>
          <a className="gamehero__link" {...linkProps(`${game.href}/how-to-play`)}>
            Read the rules →
          </a>
        </div>

        <div className="gamehero__panel">
          {solo ? (
            <SoloStartPanel
              gameType={solo.gameType}
              gameName={game.name}
              presets={solo.presets}
              choiceLabel={solo.choiceLabel}
              verb={solo.verb}
            />
          ) : (
            <JoinPanel gameName={game.name} gameType={game.id as GameType} />
          )}
        </div>
      </section>

      <section className="presets-list" aria-label={solo ? 'Difficulty' : 'House rules'}>
        <h2 className="section__title">
          <span>{solo ? solo.presetsTitle : 'Play it your way'}</span>
        </h2>
        <p className="presets-list__intro">
          {solo
            ? solo.presetsIntro
            : `Everyone's table plays ${game.name} differently, so the rules are yours to set. Start ` +
              'from a preset and change anything you like — the host sets them before starting.'}
        </p>
        <div className="presets-list__grid">
          {presets.map((preset) => (
            <div key={preset.id} className="presetcard">
              <h3 className="presetcard__name">{preset.name}</h3>
              <p className="presetcard__desc">{preset.description}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
